import { Injectable } from '@angular/core';
import {
  CarsWidget,
  LocationSearch
} from '@com-delta-idp/mnx-sitewide-header-ui';
import { HeaderFooterMachCore } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Location } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  DatePickerOptions,
  DropdownOptions
} from '@com-delta-omnichannel/mach-core-js';
import { BookingCarsAdapterSuperService } from './booking-cars-adapter-super.service';
import {
  generateTimeSlots,
  getFutureEndDate,
  getPropertyValue
} from './booking-cars-adapter.utils';

@Injectable({ providedIn: 'root' })
export class BookingCarsAdapterService extends BookingCarsAdapterSuperService {
  errorBannerMessage = '';

  getContent(aem: HeaderFooterMachCore): CarsWidget {
    this.bookingWidgetAem = aem;
    this.errorBannerMessage =
      aem?.header?.book?.cars?.error.errorBannerMessage ?? '';
    this.locationLabel =
      aem?.header?.book?.cars?.locationContent?.label ?? 'Location';
    return {
      locationContent: this.getLocation(aem),
      locationSearch: this.getLocationSearch(aem),
      pickUpDate: this.getPickupDate(aem),
      pickUpTimeDropdown: this.getPickUpTimeDropDown(aem),
      dropOffDate: this.getDropOffDate(aem),
      dropOffTimeDropdown: this.getDropOffTimeDropDown(aem),
      labels: this.getLabels(aem),
      errors: this.getErrors(aem),
      accessibility: this.getAccessibility(aem)
    };
  }

  getLocation(aem: HeaderFooterMachCore): Location {
    return {
      label: aem?.header?.book?.cars?.locationContent?.label ?? '',
      placeholder: aem?.header?.book?.cars?.locationContent?.placeholder ?? '',
      accessibilityLocation:
        aem?.header?.book?.cars?.locationContent?.accessibilityLocation ?? ''
    };
  }

  getLocationSearch(aem: HeaderFooterMachCore): LocationSearch {
    return {
      locationLabel: aem?.header?.book?.cars?.locationContent?.label ?? '',
      pickupLabel: aem?.header?.book?.cars?.locationContent?.placeholder ?? ''
    };
  }

  getPickupDate(aem: HeaderFooterMachCore): DatePickerOptions {
    return {
      id: 'pickup-date-selector',
      title: aem?.header?.book?.cars?.pickUpDate?.title ?? '',
      selectionType: 'single',
      triggerElementId: 'pickup-date-trigger',
      dates: {
        dateRange: {
          start: new Date(),
          end: getFutureEndDate()
        }
      }
    };
  }

  getPickUpTimeDropDown(aem: HeaderFooterMachCore): DropdownOptions {
    return {
      id: 'pickup-time-dropdown',
      placeholder: '',
      icon: aem?.header?.book?.cars?.pickUpTimeDropdown?.icon ?? '',
      items: generateTimeSlots(15)
    };
  }

  getDropOffDate(aem: HeaderFooterMachCore): DatePickerOptions {
    return {
      id: 'dropoff-date-selector',
      title: aem?.header?.book?.cars?.dropOffDate?.title ?? '',
      selectionType: 'single',
      triggerElementId: 'dropoff-date-trigger',
      dates: {
        dateRange: {
          start: new Date(),
          end: getFutureEndDate()
        }
      }
    };
  }

  getDropOffTimeDropDown(aem: HeaderFooterMachCore): DropdownOptions {
    return {
      id: 'dropoff-time-dropdown',
      placeholder: '',
      icon: aem?.header?.book?.cars?.dropOffTimeDropdown?.icon ?? '',
      items: generateTimeSlots(15)
    };
  }

  getErrors(aem: HeaderFooterMachCore): CarsWidget['errors'] {
    return {
      errorMessage: aem?.header?.book?.cars?.error?.errorMessage ?? '',
      locationErrorMessage:
        aem?.header?.book?.cars?.error?.locationErrorMessage ?? '',
      dateErrorMessage: aem?.header?.book?.cars?.error?.dateErrorMessage ?? '',
      dateSelectionErrorMessage:
        aem?.header?.book?.cars?.error?.dateSelectionErrorMessage,
      dateRangeErrorMessage:
        aem?.header?.book?.cars?.error?.dateRangeErrorMessage,
      timeRangeErrorMessage:
        aem?.header?.book?.cars?.error?.timeRangeErrorMessage,
      pickupTimePassedErrorMessage:
        aem?.header?.book?.cars?.error?.pickupTimePassedErrorMessage,
      dropoffTimePassedErrorMessage:
        aem?.header?.book?.cars?.error?.dropoffTimePassedErrorMessage,
      dropOffBeforePickupMessage:
        aem?.header?.book?.cars?.error?.dropOffBeforePickupMessage
    };
  }

  getAccessibility(aem: HeaderFooterMachCore): CarsWidget['accessibility'] {
    const accessibility = aem?.header?.book?.cars?.accessibility;
    return {
      locationAriaLabel: getPropertyValue(accessibility, 'locationAriaLabel'),
      pickUpAriaLabel: getPropertyValue(accessibility, 'pickUpAriaLabel'),
      pickUpTimeAriaLabel: getPropertyValue(
        accessibility,
        'pickUpTimeAriaLabel'
      ),
      dropOffTimeLabel: getPropertyValue(accessibility, 'dropOffAriaLabel'),
      dropOffTimeAriaLabel: getPropertyValue(
        accessibility,
        'dropOffTimeAriaLabel'
      ),
      buttonAriaLabel: getPropertyValue(accessibility, 'buttonAriaLabel')
    };
  }
}
